// src/components/CheckoutMatch.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  Divider,
  Typography,
  CircularProgress,
  Link,
  useMediaQuery,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import AppleIcon from '@mui/icons-material/Apple';
import { CardElement, useRecurly } from '@recurly/react-recurly';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import AdvisoryCloudLogo from '../../components/AdvisoryCloudLogo';
import TrustPilotBanner from '../../components/TrustPilotBanner/v2';
import { signUp, getRecurlyPlanOpen } from '../../services/Backend/account';
import screen from './screen.png';
import person1 from './person1.png';
import person2 from './person2.png';
import TestimonialCard from './TestimonialCard';
import StepsBanner from './StepsBanner';
import './styles.css';
import { gtmCheckoutPublicEvent } from '../../services/GTM';

const bullets = [
  'Guaranteed board placement given our exclusive relationships.',
  'Free board matching and setup call to optimize your Advisor Profile and Advisor Rate.',
  'Leverage your Advisor Profile to connect with new professional opportunities including project based advisory work.',
];

const testimonials = [
  {
    description:
      'After a short time using their platform, I have been placed in an advisory board role! The platform is easy to navigate.',
    name: 'Tammy Martin',
    title: 'CEO & Advisor',
    image: person2,
  },
  {
    description:
      'AdvisoryCloud helped me find new and exciting advisory work with a company I wouldn’t have met otherwise.',
    name: 'Danny Stroud',
    title: 'COO, Neptune Mobile',
    image: person1,
  },
];

let hideApplePayEmail = false;

const CheckoutMatch = () => {
  const location = useLocation();
  const recurly = useRecurly();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const mountedRef = useRef(true);
  const execRef = useRef(null); // para evitar closures con recaptcha
  const applePayInitRef = useRef(false); // prevenir doble init de Apple Pay

  const [applePaySupported, setApplePaySupported] = useState(true);
  const [isRecaptchaReady, setIsRecaptchaReady] = useState(!!executeRecaptcha);

  const [planData, setPlanData] = useState(null);
  const [form, setForm] = useState({
    givenName: '',
    surName: '',
    email: '',
    postalCode: '',
    acceptTerms: true,
    phoneNumber: '5555555555',
    country: 'US',
  });
  const [utmParams, setUtmParams] = useState({});
  const [cardValid, setCardValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [applePayError, setApplePayError] = useState('');

  const recurlyPlanCode = 'jb-m-coo-ft-pro-limited';
  const testApplePayPlancode = 'jb-apple-pay-testing';
  const isXS = useMediaQuery((theme) => theme.breakpoints.down('xs'));

  // Mantener executeRecaptcha en un ref
  useEffect(() => {
    execRef.current = executeRecaptcha || null;
    if (executeRecaptcha) setIsRecaptchaReady(true);
  }, [executeRecaptcha]);

  // Cleanup de montaje
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const raiseError = useRef((msg) => {});
  useEffect(() => {
    raiseError.current = (msg) => {
      console.log('raiseError called with msg =', msg);
      console.log('mountedRef.current =', mountedRef.current);
      if (!mountedRef.current) {
        document.getElementById('apple-pay-error').innerText = msg;
        setApplePayError('');
      } else {
        document.getElementById('apple-pay-error').innerText = '';
        setApplePayError(String(msg || 'Something went wrong'));
      }
      console.log('Setting applePayError state');
      setLoading(false);
    };
  }, []);

  // Helper: obtener token de Recaptcha (espera breve si aún no está listo)
  const getRecaptchaToken = useCallback(async (action = 'signUp') => {
    for (let i = 0; i < 10; i++) {
      if (execRef.current) {
        return await execRef.current(action);
      }
      await new Promise((r) => setTimeout(r, 150));
    }
    throw new Error('reCAPTCHA not ready');
  }, []);

  // Extraer UTM y prefills desde URL + inicializar Apple Pay (una sola vez)
  useEffect(() => {
    if (!isRecaptchaReady || !mountedRef.current) return;

    const params = new URLSearchParams(location.search);
    const mapping = {
      first_name: 'givenName',
      last_name: 'surName',
      email: 'email',
    };
    const newForm = { ...form };
    Object.entries(mapping).forEach(([key, field]) => {
      if (params.has(key)) newForm[field] = params.get(key);
    });
    setForm(newForm);
    setUtmParams({
      utmAdGroup: params.get('utm_adgroup') || '',
      utmCampaign: params.get('utm_campaign') || '',
      utmContent: params.get('utm_content') || '',
      utmDevice: params.get('utm_device') || '',
      utmKeyword: params.get('utm_keyword') || '',
      utmMatchType: params.get('utm_matchtype') || '',
      utmMedium: params.get('utm_medium') || '',
      utmNetwork: params.get('utm_network') || '',
      utmPlacement: params.get('utm_placement') || '',
      utmSource: params.get('utm_source') || '',
    });
    if (params.get('email')) hideApplePayEmail = true;

    if (applePayInitRef.current) return;
    applePayInitRef.current = true;

    let cleanupButtonListener = null;

    try {
      const applePay = recurly.ApplePay({
        country: 'US',
        currency: 'USD',
        label: 'AdvisoryCloud Verification', // This text appears on the Apple Pay payment sheet
        total: '0.00',
        requiredBillingContactFields: ['email'],
        callbacks: {
          onPaymentAuthorized: async (event) => {
            try {
              const recurlyToken = event.payment.recurlyToken.id;

              // Recaptcha
              const recaptchaToken = await getRecaptchaToken('signUp');

              // Forzar email desde el input si existe
              let textBox = document.getElementById('email');
              form.email = textBox?.value || form.email;

              // Completar datos del contacto (si Apple Pay los trae)
              form.givenName =
                event.payment?.billingContact?.givenName || form.givenName;
              form.surName =
                event.payment?.billingContact?.familyName || form.surName;
              form.postalCode =
                event.payment?.billingContact?.postalCode || form.postalCode;
              const params = new URLSearchParams(location.search);

              const payload = {
                ...form,
                utmAdGroup: params.get('utm_adgroup') || '',
                utmCampaign: params.get('utm_campaign') || '',
                utmContent: params.get('utm_content') || '',
                utmDevice: params.get('utm_device') || '',
                utmKeyword: params.get('utm_keyword') || '',
                utmMatchType: params.get('utm_matchtype') || '',
                utmMedium: params.get('utm_medium') || '',
                utmNetwork: params.get('utm_network') || '',
                utmPlacement: params.get('utm_placement') || '',
                utmSource: params.get('utm_source') || '',
                companyName:
                  form.companyName || `${form.givenName} ${form.surName}`,
                phoneCountryCode: form.country,
                recurlyBillingToken: recurlyToken,
                recurlyPlanCode: recurlyPlanCode,
                urlOfCheckoutPage: window.location.href,
                acceptTermsOfService: form.acceptTerms,
                recaptchaToken,
              };

              const res = await signUp(payload);

              try {
                if (res.errors && res.errors.length > 0) {
                  throw JSON.parse(res.errors[0].message).error;
                }
                const checkoutRedirectUrl =
                  res?.data?.signUp?.checkoutRedirectUrl;
                const accountType = res?.data?.signUp?.accountType;
                console.log('Checkout redirect URL:', checkoutRedirectUrl);
                console.log('Account type:', accountType);
                if (checkoutRedirectUrl) {
                  window.location.assign(
                    checkoutRedirectUrl + window.location.search
                  );
                } else {
                  window.location.assign(
                    `/${
                      accountType === 'B2B' ? 'company' : 'advisor'
                    }/confirmation?email=${form.email}`
                  );
                }
              } catch (err) {
                raiseError.current(
                  err?.message === 'reCAPTCHA not ready'
                    ? 'Security check unavailable. Please refresh the page and try again.'
                    : err?.message || err?.error || 'Something went wrong'
                );
              }
            } catch (err) {
              raiseError.current(
                err?.message === 'reCAPTCHA not ready'
                  ? 'Security check unavailable. Please refresh the page and try again.'
                  : err?.message || err?.error || 'Something went wrong'
              );
              setLoading(false);
            }
          },
        },
      });

      applePay.ready(function () {
        const button = document.getElementById('applePay');
        if (!button) return;
        const handler = function () {
          applePay.begin();
        };
        button.addEventListener('click', handler);
        cleanupButtonListener = () =>
          button.removeEventListener('click', handler);
      });

      applePay.on('error', function (err) {
        if (
          err.name === 'apple-pay-not-supported' ||
          err.code === 'apple-pay-not-supported'
        ) {
          setApplePaySupported(false);
        }
        setApplePayError(err.message || 'Apple Pay error');
      });
    } catch (err) {
      // no-op, ya se maneja arriba
      // console.log(err);
    }

    return () => {
      if (cleanupButtonListener) cleanupButtonListener();
    };
  }, [isRecaptchaReady]);

  // Cargar datos del plan
  useEffect(() => {
    getRecurlyPlanOpen(recurlyPlanCode).then((res) => setPlanData(res));
    gtmCheckoutPublicEvent('jb-quiz-checkout-match');
  }, []);

  const handleChange = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const isValid = () => {
    if (!planData) return false;
    const basic = form.givenName && form.surName && form.email;
    if (planData.isFreemium) return basic;
    return basic && form.postalCode && cardValid;
  };

  const isEmailValid = () => {
    if (!planData) return false;
    return (
      form.email &&
      String(form.email)
        .toLowerCase()
        .match(
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        )
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const recaptchaToken = await getRecaptchaToken('signUp');

      // Token de Recurly (card flow)
      const recurlyForm = {
        first_name: form.givenName,
        last_name: form.surName,
        postal_code: form.postalCode,
        country: form.country,
      };

      // Card flow
      recurly.token(recurlyForm, async (err, token) => {
        if (err) {
          setError(err.message || 'Something went wrong');
          setLoading(false);
          return;
        }
        const res = await signUp({
          ...form,
          ...utmParams,
          companyName: form.companyName || `${form.givenName} ${form.surName}`,
          phoneCountryCode: form.country,
          recurlyBillingToken: token.id,
          recurlyPlanCode: recurlyPlanCode,
          urlOfCheckoutPage: window.location.href,
          acceptTermsOfService: form.acceptTerms,
          recaptchaToken,
        });
        try {
          if (res.errors && res.errors.length > 0) {
            setError(
              JSON.parse(res.errors[0].message).error.message ||
                'Something went wrong'
            );
            setLoading(false);
            return;
          }
          const checkoutRedirectUrl = res?.data?.signUp?.checkoutRedirectUrl;
          const accountType = res?.data?.signUp?.accountType;
          console.log('Checkout redirect URL:', checkoutRedirectUrl);
          console.log('Account type:', accountType);
          if (checkoutRedirectUrl) {
            window.location.assign(
              checkoutRedirectUrl + window.location.search
            );
          } else {
            window.location.assign(
              `/${
                accountType === 'B2B' ? 'company' : 'advisor'
              }/confirmation?email=${form.email}`
            );
          }
        } catch (error) {
          setLoading(false);
          return;
        }
      });
    } catch (err) {
      setError(
        err?.message === 'reCAPTCHA not ready'
          ? 'Security check unavailable. Please refresh the page and try again.'
          : err.message || 'Something went wrong'
      );
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mt: '-30px',
          mb: { xs: -3, xxs: -5 },
        }}
      >
        <AdvisoryCloudLogo removeHyperlink width={isXS ? 180 : 185} />
      </Box>
      <StepsBanner />

      <Grid container spacing={6}>
        <Grid item xxs={12} md={6}>
          <Box
            sx={{
              maxWidth: '645px',
              ml: 'auto',
              mr: { xxs: 'auto', md: '15px' },
              px: 3,
            }}
          >
            <Typography
              sx={{
                fontSize: { xxs: '25px', xs: '48px' },
                fontWeight: 800,
                mb: 3,
              }}
            >
              You have{' '}
              <Box component="span" sx={{ color: 'rgb(20,120,248)' }}>
                74+
              </Box>{' '}
              potential board matches!
            </Typography>

            <Box sx={{ width: '100%', borderRadius: 2, mb: 4 }}>
              <img
                src={screen}
                alt="Checkout Mockup"
                style={{ width: '100%', borderRadius: '16px' }}
              />
            </Box>

            {bullets.map((txt, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  mb: 2,
                  bgcolor: '#f1f9ff',
                  borderRadius: '8px',
                  p: 2,
                  alignItems: 'center',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    minWidth: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: 'rgb(0, 84, 139)',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: '1rem',
                    alignSelf: 'center',
                  }}
                >
                  {i + 1}
                </Box>
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ mx: 2, width: 2, bgcolor: 'rgb(0, 84, 139)' }}
                />
                <Typography
                  sx={{
                    color: 'rgb(0, 84, 139)',
                    fontSize: { xs: '14px', md: '16px' },
                    lineHeight: 1.4,
                  }}
                >
                  {txt}
                </Typography>
              </Box>
            ))}
          </Box>
        </Grid>

        <Grid item xxs={12} md={6} sx={{ px: '0px' }}>
          <Box
            sx={{
              maxWidth: '645px',
              mr: 'auto',
              ml: { xxs: 'auto', md: '15px' },
              px: 3,
            }}
          >
            <Box
              sx={{
                bgcolor: 'rgba(43,113,246,1)',
                color: 'white',
                p: 2,
                textAlign: 'center',
                mb: 2,
                fontSize: '20px',
                fontWeight: 800,
              }}
            >
              See why over 25,000+ Directors, VPs &amp; CXOs are on
              AdvisoryCloud.
            </Box>

            <TrustPilotBanner />

            <Typography sx={{ mt: 3, mb: 2, fontSize: '20px' }}>
              <strong>14-Day Free Trial</strong>, then just $195/month. Cancel
              anytime.
            </Typography>

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Grid container spacing={2}>
                <Grid item xxs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    value={form.givenName}
                    onChange={handleChange('givenName')}
                  />
                </Grid>
                <Grid item xxs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    value={form.surName}
                    onChange={handleChange('surName')}
                  />
                </Grid>
                <Grid item xxs={12}>
                  <TextField
                    id="email"
                    fullWidth
                    label="Email"
                    value={form.email}
                    onChange={handleChange('email')}
                  />
                </Grid>

                {planData?.accountType === 'B2C' && !planData.isFreemium && (
                  <>
                    <Box id="recurly-card" sx={{ width: '100%', ml: 2, mt: 1 }}>
                      <CardElement onChange={(e) => setCardValid(e.valid)} />
                    </Box>
                    <Grid item xxs={12}>
                      <TextField
                        fullWidth
                        label="Billing Zip Code"
                        value={form.postalCode}
                        onChange={handleChange('postalCode')}
                      />
                    </Grid>
                  </>
                )}

                {error && (
                  <Grid item xxs={12}>
                    <Typography color="error">{error}</Typography>
                  </Grid>
                )}

                <Grid item xxs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    sx={{
                      bgcolor: 'rgb(8,114,253)',
                      color: 'white',
                      borderRadius: '40px',
                      fontSize: '18px',
                      fontWeight: 700,
                      py: 1.5,
                      '&:disabled': {
                        bgcolor: 'rgb(8,114,253)',
                        color: 'white',
                        cursor: 'not-allowed',
                        opacity: 0.5,
                      },
                    }}
                    disabled={!isValid() || loading}
                  >
                    {loading ? (
                      <CircularProgress size={24} />
                    ) : (
                      'Start Your Free Trial'
                    )}
                  </Button>
                </Grid>

                {applePaySupported && (
                  <>
                    <Grid item xxs={12}>
                      <Box sx={{ my: 2 }}>
                        <Divider textAlign="center">
                          <Typography
                            component="span"
                            sx={{ fontSize: '0.875rem' }}
                          >
                            or use Express Checkout
                          </Typography>
                        </Divider>
                      </Box>
                    </Grid>

                    {!hideApplePayEmail && (
                      <Grid item xxs={12}>
                        <TextField
                          fullWidth
                          label="Email"
                          value={form.email}
                          onChange={handleChange('email')}
                        />
                      </Grid>
                    )}

                    <Grid item xxs={12}>
                      <Button
                        type="button"
                        id="applePay"
                        variant="contained"
                        fullWidth
                        sx={{
                          bgcolor: 'black',
                          color: 'white',
                          borderRadius: '40px',
                          fontSize: '18px',
                          fontWeight: 700,
                          py: 1.5,
                          '&:hover': { bgcolor: '#000000ff' },
                          '&:disabled': { opacity: 0.5 },
                        }}
                        disabled={
                          loading || !execRef.current || !isEmailValid()
                        }
                      >
                        {loading ? (
                          <CircularProgress size={24} />
                        ) : (
                          <>
                            Buy with&nbsp;
                            <AppleIcon
                              sx={{ fontSize: 24, verticalAlign: 'middle' }}
                            />
                            &nbsp;Pay
                          </>
                        )}
                      </Button>
                      <Typography
                        color="#313C4F"
                        textAlign="center"
                        mt={1}
                        fontSize={'11px'}
                      >
                        You may see a temporary $1 authorization from your bank
                        to verify your card
                      </Typography>
                      {applePayError && (
                        <Typography color="error" textAlign="center" mt={1}>
                          {applePayError}
                        </Typography>
                      )}
                      <Typography
                        color="error"
                        textAlign="center"
                        mt={1}
                        id="apple-pay-error"
                      ></Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>

            <Box sx={{ p: 3 }}>
              <Typography sx={{ display: 'flex', mb: 1, fontSize: '18px' }}>
                <Box component="span" sx={{ mr: 1 }}>
                  ✓
                </Box>
                We will NOT bill your credit card for 14 days. Guaranteed.
              </Typography>
              <Typography sx={{ display: 'flex', mb: 1, fontSize: '18px' }}>
                <Box component="span" sx={{ mr: 1 }}>
                  ✓
                </Box>
                After 14 days your membership renews monthly at $195.
              </Typography>
              <Typography
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  mb: 1,
                  fontSize: '18px',
                }}
              >
                ✓ By starting your free trial, you agree to our&nbsp;
                <Link
                  href="https://www.advisorycloud.com/terms-of-service"
                  target="_blank"
                  rel="noopener"
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Terms & Privacy
                </Link>
                .
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Box
        sx={{
          p: 3,
          maxWidth: '1350px',
          mx: 'auto',
          display: 'flex',
          flexDirection: { xxs: 'column', xs: 'row' },
          gap: 2,
        }}
      >
        {testimonials.map((t, i) => (
          <TestimonialCard
            key={i}
            description={t.description}
            name={t.name}
            title={t.title}
            image={t.image}
          />
        ))}
      </Box>
    </Box>
  );
};

export default CheckoutMatch;
